import { ReactNode, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MARKET } from '@/common/constants/markets';
import { FamilyMemberFormModal } from '@/common/containers/Dashboard/Profile/FamilyMembers/components/FamilyMemberFormModal/FamilyMemberFormModal';
import { useGetInsuranceDuplicatedFamilyMembers } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import {
  TInsuranceDuplicatedMember,
  TInsurancePerson,
  TInsuranceUpgradePlan,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import {
  InsuranceUpgradeLearningCentre,
  TInsuranceUpgradeLearningCentreProps,
} from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/InsurancePlanChooseStep/components/InsuranceUpgradeLearningCentre/InsuranceUpgradeLearningCentre';
import {
  InsuranceUpgradePlanCard,
  TInsuranceUpgradePlanCardProps,
} from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/InsurancePlanChooseStep/components/InsuranceUpgradePlanCard/InsuranceUpgradePlanCard';
import { InsuranceUpgradePlanConfirmationModal } from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/InsurancePlanChooseStep/components/InsuranceUpgradePlanConfirmationModal/InsuranceUpgradePlanConfirmationModal';
import { InsuranceUpgradePlanGeneralConditions } from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/InsurancePlanChooseStep/components/InsuranceUpgradePlanGeneralConditions/InsuranceUpgradePlanGeneralConditions';
import {
  calculateTotals,
  shouldShowInsuranceUpgradeConfirmationModal,
  useShouldDisplayFamilyMembersUI,
} from '@/web/containers/Insurance/InsuranceUpgrade/common/utils';
import { useMarket } from '@/web/hooks/useMarket';
import {
  Alert,
  Button,
  Flex,
  Grid,
  Heading,
  IconUserPlus,
} from '@coverflex-tech/hypernova';
import { InsuranceNotIncludedFamilyMembersModal } from './components/InsuranceUpgradePlanConfirmationModal/components/InsuranceNotIncludedFamilyMembersModal/InsuranceNotIncludedFamilyMembersModal';
import { css } from './InsurancePlanChooseStep.css';

export const InsurancePlanChooseStep = ({
  title,
  selectedPlan,
  basePlan,
  plans = [],
  limitDate,
  onTogglePerson,
  onSelectPlan,
  onSubmit,
  onNext,
}: TInsuranceUpgradePlanChooseProps) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { slug } = useMarket();
  const { shouldDisplayFamilyMembersUI } = useShouldDisplayFamilyMembersUI();
  const { duplicatedFamilyMembers } = useGetInsuranceDuplicatedFamilyMembers();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [validateIncludedFamilyMembers, setValidateIncludedFamilyMembers] =
    useState(false);
  const maxComparableCoverages = useMemo(
    () =>
      Math.max(
        ...plans.map((p) => p.coverages.filter((c) => c.comparable).length)
      ),
    [plans]
  );
  const maxNonComparableCoverages = useMemo(
    () =>
      Math.max(
        ...plans.map((p) => p.coverages.filter((c) => !c.comparable).length)
      ),
    [plans]
  );

  const displayFamilyMembersUI = shouldDisplayFamilyMembersUI(basePlan);

  const onChoosingPlan = (plan: TInsuranceUpgradePlan) => {
    onSelectPlan?.(plan);
    if (
      shouldShowInsuranceUpgradeConfirmationModal(
        plan,
        duplicatedFamilyMembers ?? [],
        displayFamilyMembersUI
      )
    ) {
      setShowConfirmationModal(true);
    } else {
      if (plan.persons.some((p) => !p.included)) {
        setValidateIncludedFamilyMembers(true);
      } else onNext?.();
    }
  };

  const filteredPersons = (
    persons: TInsurancePerson[],
    duplicatedFamilyMembers: TInsuranceDuplicatedMember[]
  ): TInsurancePerson[] => {
    return persons.filter((person) => {
      return !duplicatedFamilyMembers.some(
        (duplicated) =>
          duplicated.id === person.id && duplicated.alreadyInsured === true
      );
    });
  };

  const filteredFamilyMembers = filteredPersons(
    selectedPlan?.persons ?? [],
    duplicatedFamilyMembers ?? []
  );

  const handleNext = () => {
    const hasNotIncludedFamilyMember = filteredFamilyMembers.some(
      (p) => !p.included
    );

    if (hasNotIncludedFamilyMember) {
      setValidateIncludedFamilyMembers(true);
      setShowConfirmationModal(false);
    } else {
      onNext?.();
    }
  };

  const handleSubmit = () => {
    if (selectedPlan?.persons?.some((p) => !p.included)) {
      setValidateIncludedFamilyMembers(true);
    } else if (slug === MARKET.PT) {
      onSubmit?.();
    } else {
      onNext?.();
    }
    setShowConfirmationModal(false);
  };

  const selectedPlanIndex = plans.findIndex((p) => p.selected);

  const { totalEmployeeCost } = calculateTotals(selectedPlan?.persons ?? []);

  const shouldShowBadge = (plan: TInsuranceUpgradePlan) => {
    return slug === MARKET.PT && plan.policyId === basePlan.policyId;
  };

  const paymentType = (() => {
    if (basePlan.paidByCompany.spouse && basePlan.paidByCompany.child) {
      return 'pay_none';
    }
    if (basePlan.paidByCompany.spouse) {
      return 'pay_child';
    }
    if (basePlan.paidByCompany.child) {
      return 'pay_spouse';
    }
    return 'pay_all';
  })();

  return (
    <Flex flexDirection="column" gap="lg" justifyContent="flex-start">
      {showConfirmationModal && selectedPlan && (
        <InsuranceUpgradePlanConfirmationModal
          planName={selectedPlan.name}
          enrolEndDate={limitDate}
          persons={selectedPlan.persons}
          displayFamilyMembersUI={displayFamilyMembersUI}
          onAddFamilyMembers={() => {
            setShowModal(true);
          }}
          allDuplicatedFamilyMembers={duplicatedFamilyMembers ?? []}
          onClose={() => setShowConfirmationModal(false)}
          onNext={handleNext}
          onSubmit={handleSubmit}
        />
      )}
      {validateIncludedFamilyMembers && selectedPlan && (
        <InsuranceNotIncludedFamilyMembersModal
          planName={selectedPlan.name}
          familyMembers={filteredPersons(
            selectedPlan.persons,
            duplicatedFamilyMembers ?? []
          )}
          enrolEndDate={limitDate}
          onClose={() => setValidateIncludedFamilyMembers(false)}
          onAction={() => {
            if (totalEmployeeCost.amount === 0 && slug === MARKET.PT) {
              onSubmit?.();
            } else {
              onNext?.();
            }
            setValidateIncludedFamilyMembers(false);
          }}
        />
      )}
      {slug === MARKET.PT && (
        <InsuranceUpgradeLearningCentre
          startDate={basePlan.startDate}
          limitDate={limitDate}
          hideFamilyEducationalPoint={!displayFamilyMembersUI}
        />
      )}
      <Flex flexDirection="column" gap="lg">
        <Flex alignItems="center" justifyContent="space-between">
          <Heading level={3}>{title}</Heading>
          {displayFamilyMembersUI && (
            <Button icon={<IconUserPlus />} onClick={() => setShowModal(true)}>
              {t('dashboard.insurance.card.add_action')}
            </Button>
          )}
        </Flex>
        {displayFamilyMembersUI && slug === MARKET.PT && (
          <Alert type="info">
            <Trans
              i18nKey={`dashboard.insurance.upgrade_plan.family_members.company_coverage.description.${paymentType}`}
            />
          </Alert>
        )}
      </Flex>
      <Grid.Root
        auto
        minColumnWidth="30rem"
        maxColumnWidth="33rem"
        gap="lg"
        fillMode="auto-fit"
        className={css.grid}
      >
        {plans
          .slice(selectedPlanIndex === -1 ? 0 : selectedPlanIndex)
          .map((plan) => (
            <InsuranceUpgradePlanCard
              reservedComparableRowCount={maxComparableCoverages}
              reservedNonComparableRowCount={maxNonComparableCoverages}
              key={plan.policyId}
              name={plan.name}
              persons={plan.persons}
              coverages={plan.coverages}
              hidePeopleList={!displayFamilyMembersUI}
              onTogglePerson={onTogglePerson}
              showBasePlanBadge={shouldShowBadge(plan)}
              onSelect={() => onChoosingPlan(plan)}
              files={plan.files}
            />
          ))}
      </Grid.Root>
      <InsuranceUpgradePlanGeneralConditions
        href={basePlan.files?.find((f) => f.slug === 'general_conditions')?.url}
      />
      {showModal && (
        <FamilyMemberFormModal onClose={() => setShowModal(false)} />
      )}
    </Flex>
  );
};

export type TInsuranceUpgradePlanChooseProps = {
  title: ReactNode;
  plans?: TInsuranceUpgradePlan[];
  selectedPlan?: TInsuranceUpgradePlan;
  basePlan: TInsuranceUpgradePlan;
  limitDate?: TInsuranceUpgradeLearningCentreProps['limitDate'];
  onTogglePerson?: TInsuranceUpgradePlanCardProps['onTogglePerson'];
  onSelectPlan?: (plan: TInsuranceUpgradePlan) => void;
  onSubmit?: () => void;
  onNext?: () => void;
};

export type TFilteredPerson = {
  persons: TInsurancePerson[];
};
